import { AppModal, Button, Text } from "@/components/ui";
import type { PhaseActionTaskTemplate } from "@/lib/types";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PhaseActionTaskTemplateEditor } from "./PhaseActionTaskTemplateEditor";

export function PhaseActionTaskTemplatesModal({
  actionTitle,
  templates,
  onChange,
  onClose,
  readOnly,
}: {
  actionTitle: string;
  templates: PhaseActionTaskTemplate[];
  onChange: (templates: PhaseActionTaskTemplate[]) => void;
  onClose: () => void;
  readOnly: boolean;
}) {
  const settings = useQuery(retrieveMatterSettingsQueryOptions());
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const roles = settings.data?.data.roles ?? [];

  if (
    (templates.length === 1 || (!readOnly && templates.length === 0)) &&
    !settings.isPending &&
    !settings.isError
  ) {
    return (
      <PhaseActionTaskTemplateEditor
        open
        readOnly={readOnly}
        template={templates[0]}
        roles={roles}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        onSave={(template) => {
          onChange([template]);
          onClose();
        }}
        onRemove={
          templates.length === 1
            ? () => {
                onChange([]);
                onClose();
              }
            : undefined
        }
      />
    );
  }

  return (
    <>
      <AppModal
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={`Automatic task for ${actionTitle}`}
        description="One task can be linked to this action on each matter. Save the project to apply this configuration. Changes leave the existing task unchanged."
        size="2xl"
        overlayClassName="z-[104]"
        className="z-[105]"
        footer={
          <div className="flex w-full justify-end">
            <Button type="button" variant="outline" onClick={onClose}>
              Done
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {templates.length === 0 ? (
            <Text variant="caption">No automatic task configured.</Text>
          ) : (
            templates.map((template, index) => (
              <div
                key={template.id ?? index}
                className="flex items-center justify-between gap-3 rounded-lg border p-3"
              >
                <div className="min-w-0">
                  <Text variant="body" className="font-medium break-words">
                    {template.title}
                  </Text>
                  <Text variant="caption">
                    {template.assigneeRoleId
                      ? `Assignee: ${roles.find((role) => role.id === template.assigneeRoleId)?.label ?? "Matter role"}`
                      : template.assigneeUserIds.length
                        ? `${template.assigneeUserIds.length} selected user(s)`
                        : "Created unassigned"}
                    {template.dueDate !== null
                      ? ` · Due ${template.dueDate} day(s) after action is added`
                      : ""}
                  </Text>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingIndex(index);
                    }}
                  >
                    {readOnly ? "View" : "Edit"}
                  </Button>
                  {!readOnly ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      aria-label={`Remove automatic task ${template.title}`}
                      onClick={() => {
                        onChange(
                          templates.filter(
                            (_, itemIndex) => itemIndex !== index,
                          ),
                        );
                      }}
                    >
                      Remove
                    </Button>
                  ) : null}
                </div>
              </div>
            ))
          )}
          {settings.isError ? (
            <div className="space-y-2">
              <Text variant="caption">Unable to load assignment settings.</Text>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  void settings.refetch();
                }}
              >
                Retry
              </Button>
            </div>
          ) : null}
          {templates.length > 1 ? (
            <Text variant="caption">
              Only one automatic task is allowed. Remove the extra templates
              before saving the Project.
            </Text>
          ) : null}
        </div>
      </AppModal>
      <PhaseActionTaskTemplateEditor
        readOnly={readOnly}
        open={editingIndex !== null}
        onOpenChange={(open) => {
          if (!open) setEditingIndex(null);
        }}
        template={editingIndex !== null ? templates[editingIndex] : undefined}
        roles={roles}
        onSave={(template) => {
          if (editingIndex === null) return;
          onChange(
            editingIndex < templates.length
              ? templates.map((item, index) =>
                  index === editingIndex ? template : item,
                )
              : [...templates, template],
          );
        }}
      />
    </>
  );
}
